import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuIcon, XIcon, SparklesIcon } from './Icons';
import Logo from './Logo';
import { APP_CONFIG } from '../data/content';

const SunIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MoonIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
  </svg>
);

const Header = ({ projectName, onNavigate }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
    if (nextTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setIsMenuOpen(false);

    if (location.pathname === '/') {
      if (onNavigate) {
        onNavigate(sectionId);
      } else {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'glass-panel shadow-lg shadow-black/5 dark:shadow-black/20 py-3 border-b' 
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 flex justify-between items-center">
        {/* Brand Logo */}
        <div 
          className="flex items-center gap-3 cursor-pointer select-none group"
          onClick={() => {
            if (location.pathname === '/') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/');
            }
          }}
        >
          <div className="relative">
            <Logo className="w-9 h-9 transform transition-transform duration-300 group-hover:scale-105" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-900 dark:text-white tracking-tight font-outfit">
                {projectName}
              </span>
              <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-500/10 text-brand-600 dark:text-brand-300 border border-brand-500/20">
                Supreme AI
              </span>
            </div>
            <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 hidden sm:block tracking-wide">
              Autonomous Agent Orchestrator
            </p>
          </div>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-7">
          <a 
            href="#simulator" 
            onClick={(e) => handleNavClick(e, 'simulator')}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors duration-200"
          >
            Live Simulator
          </a>
          <a 
            href="#agents" 
            onClick={(e) => handleNavClick(e, 'agents')}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors duration-200"
          >
            Agent Fleet
          </a>
          <a 
            href="#pillars" 
            onClick={(e) => handleNavClick(e, 'pillars')}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors duration-200"
          >
            Architecture
          </a>
          <a 
            href="#integrations" 
            onClick={(e) => handleNavClick(e, 'integrations')}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors duration-200"
          >
            SaaS Tools
          </a>
          <a 
            href="#sdk" 
            onClick={(e) => handleNavClick(e, 'sdk')}
            className="text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors duration-200"
          >
            Chatbot SDK
          </a>

          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className="p-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors rounded-xl flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800/80 active:scale-95"
            aria-label="Toggle Theme"
            title="Toggle Light/Dark Theme"
          >
            {theme === 'dark' ? (
              <SunIcon className="w-4 h-4 text-amber-400" />
            ) : (
              <MoonIcon className="w-4 h-4 text-slate-700" />
            )}
          </button>

          {/* Studio Link Button */}
          <a 
            href={APP_CONFIG.studioUrl}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 shadow-md shadow-brand-500/25 transition-all duration-200 active:scale-95 border border-brand-400/30"
          >
            <SparklesIcon className="w-3.5 h-3.5 text-cyan-200" />
            <span>Launch Studio</span>
          </a>

          {/* Schedule Demo Button */}
          <a 
            href="#contact"
            onClick={(e) => handleNavClick(e, 'contact')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 glass-panel hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 active:scale-95"
          >
            <span>Book Demo</span>
          </a>
        </nav>
        
        {/* Mobile Navigation Controls */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors rounded-xl flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95"
            aria-label="Toggle Theme"
          >
            {theme === 'dark' ? (
              <SunIcon className="w-4 h-4 text-amber-400" />
            ) : (
              <MoonIcon className="w-4 h-4 text-slate-700" />
            )}
          </button>
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-95"
            aria-label="Open Menu"
          >
            {isMenuOpen ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden glass-panel border-t border-slate-200/80 dark:border-slate-800 mt-2 px-6 py-5 flex flex-col space-y-4 shadow-xl"
          >
            <a 
              href="#simulator" 
              onClick={(e) => handleNavClick(e, 'simulator')}
              className="text-base font-medium text-slate-700 dark:text-slate-200 py-1"
            >
              Live Simulator
            </a>
            <a 
              href="#agents" 
              onClick={(e) => handleNavClick(e, 'agents')}
              className="text-base font-medium text-slate-700 dark:text-slate-200 py-1"
            >
              Agent Fleet
            </a>
            <a 
              href="#pillars" 
              onClick={(e) => handleNavClick(e, 'pillars')}
              className="text-base font-medium text-slate-700 dark:text-slate-200 py-1"
            >
              Core Architecture
            </a>
            <a 
              href="#integrations" 
              onClick={(e) => handleNavClick(e, 'integrations')}
              className="text-base font-medium text-slate-700 dark:text-slate-200 py-1"
            >
              SaaS Tools
            </a>
            <a 
              href="#sdk" 
              onClick={(e) => handleNavClick(e, 'sdk')}
              className="text-base font-medium text-slate-700 dark:text-slate-200 py-1"
            >
              Chatbot SDK
            </a>
            <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex flex-col gap-2">
              <a 
                href={APP_CONFIG.studioUrl}
                className="w-full text-center py-2.5 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-brand-600 to-indigo-600 active:scale-95 shadow-md shadow-brand-500/20"
              >
                Launch Cortex Studio
              </a>
              <a 
                href="#contact"
                onClick={(e) => handleNavClick(e, 'contact')}
                className="w-full text-center py-2 rounded-xl font-semibold text-xs text-slate-700 dark:text-slate-300 glass-panel"
              >
                Book a Demo
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
